import React, { useCallback, useMemo } from 'react'
import { formatDateTime, DATE_TIME_SHORT } from '@vend/utilities'
import { useCurrentUser } from '@vend/react-utilities'
import { ApiOutlet, ApiRegister, ApiUser } from '@vend/types'
import { reactular } from '@vendhq/reactular'

import {
  DefaultTabOverflowPopoverContent,
  Modal,
  ModalContent,
  ModalHeader,
  ModalSize,
  SelectedTabProvider,
  TabContent,
  Tabs,
} from '@lightspeed/design-system-react'
import { useT } from '@transifex/react'
import classNames from 'classnames'

interface TabItem {
  id: string
  name: string
}

interface RegistersByOutlet {
  outlet: ApiOutlet
  registers: ApiRegister[]
}

interface SelectRegisterProps {
  onClose: () => {}
  size: ModalSize
  dismissable: boolean
  registersByOutlet: RegistersByOutlet[]
  selectedOutletIndex: number
  selectedRegister?: ApiRegister
  onSelectRegister: (register: ApiRegister, outlet: ApiOutlet) => {}
  getRegisterTradingStatus: (register: ApiRegister, outlet: ApiOutlet) => {}
}

const formatDateAndTime = (dateTime: string | null) => {
  if (!dateTime) {
    return
  }
  return formatDateTime(dateTime, DATE_TIME_SHORT)
}

export const SelectRegisterModalView: React.FunctionComponent<SelectRegisterProps> = ({
  onClose,
  size,
  dismissable,
  registersByOutlet,
  selectedOutletIndex,
  selectedRegister,
  onSelectRegister,
}) => {
  const t = useT()
  const user: ApiUser | null = useCurrentUser()
  const tabArray: TabItem[] = useMemo(() => {
    return registersByOutlet.map(item => {
      return {
        id: item.outlet.id,
        name: item.outlet.name,
      }
    })
  }, [registersByOutlet])

  // settings to save tabs order to localStorage so the order is maintained on mount
  const tabsLocalStorageSettings = useMemo(() => {
    if (!user) {
      return undefined
    }

    return {
      componentKey: 'WebRegisterSelectRegister',
      dataKey: user.id,
    }
  }, [user])

  const getRegisterTradingStatusText = useCallback(
    (register: ApiRegister) => {
      if (register.is_open) {
        return t('Opened at {time}', {
          _context: 'Register status text',
          time: formatDateAndTime(register.register_open_time),
        })
      }

      if (register.register_close_time !== null) {
        return t('Closed at {time}', {
          _context: 'Register status text',
          time: formatDateAndTime(register.register_close_time),
        })
      } else {
        return t('Closed', { _context: 'Register status text' })
      }
    },
    [t]
  )

  if (tabArray.length === 0) {
    return null
  }

  return (
    <Modal
      size={size}
      dismissable={dismissable}
      onClose={onClose}
      className="select-register-modal"
    >
      <ModalHeader>
        {t('Select Register', {
          context: 'Modal header',
        })}
      </ModalHeader>
      <ModalContent>
        <SelectedTabProvider defaultTab={tabArray[selectedOutletIndex].id}>
          <Tabs
            items={tabArray}
            overflowButtonText={t('More outlets…', {
              context: 'Button',
            })}
            orderPersistence={tabsLocalStorageSettings}
            renderOverflowPopoverContent={props => (
              <DefaultTabOverflowPopoverContent
                searchLabel={t('Search outlets', { _context: 'Input label' })}
                searchPlaceholder={t('Enter an outlet name', {
                  _context: 'Input placeholder',
                })}
                {...props}
              />
            )}
          />
          {registersByOutlet.map(item => (
            <TabContent key={item.outlet.id} name={item.outlet.id}>
              <div className="wr-cards vd-mt3">
                {item.registers.map(register => (
                  <button
                    key={register.id}
                    onClick={() => {
                      onSelectRegister(register, item.outlet)
                    }}
                    className={classNames('wr-card', {
                      'wr-card--active': selectedRegister?.id === register.id,
                    })}
                  >
                    <div className="wr-card-content">
                      <h3
                        className="wr-card-value"
                        data-testid={`register-name-${register.id}`}
                      >
                        {register.name}
                      </h3>
                      <div
                        className="wr-card-value-status"
                        data-testid={`register-status-${register.id}`}
                      >
                        {getRegisterTradingStatusText(register)}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </TabContent>
          ))}
        </SelectedTabProvider>
      </ModalContent>
    </Modal>
  )
}

export const SelectRegisterModalComponent = reactular(
  SelectRegisterModalView,
  [
    'onClose',
    'size',
    'dismissable',
    'registersByOutlet',
    'selectedOutletIndex',
    'selectedRegister',
    'onSelectRegister',
  ],
  'reactWrapper'
)
